import React from 'react';
import { C, THEME } from '../constants';
import OjoClave from './OjoClave';
import { login, completarLogin2FA, registrarBasico, loginWithGoogle, PERFILES_ALTA, TIPOS_INSTALADOR, destinoTrasAlta } from '../services/auth';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '786631406377-gp7ll39sp91f8n6kdfbtm19svlqlsi5k.apps.googleusercontent.com';

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN — gate de acceso de Solar Hub. Requiere sesión INCLUSO para la cotización
// básica (pedido del dueño). Credenciales UNIFICADAS de la suite Alebas (autoridad:
// api/ de FluxAI); el rol diferencia el acceso (admin → backoffice; técnico/cliente/
// proveedor → su vista). Mientras api/ no esté desplegado, el "registro para cotizar"
// abre una sesión de cliente para no bloquear el flujo.
// ─────────────────────────────────────────────────────────────────────────────

const ALTAS = [
  { perfil: PERFILES_ALTA.CLIENTE, kind: null, titulo: 'Cliente', sub: 'Cotizo o pido un estudio', dest: 'home' },
  { perfil: PERFILES_ALTA.INSTALADOR, kind: TIPOS_INSTALADOR.TECNICO, titulo: 'Técnico', sub: 'Persona · CONTE / RETIE', dest: 'instalador' },
  { perfil: PERFILES_ALTA.INSTALADOR, kind: TIPOS_INSTALADOR.INGENIERO, titulo: 'Ingeniero', sub: 'Persona · COPNIA / RETIE', dest: 'instalador' },
  { perfil: PERFILES_ALTA.INSTALADOR, kind: TIPOS_INSTALADOR.EMPRESA, titulo: 'Empresa instaladora', sub: 'La empresa también instala', dest: 'instalador' },
  { perfil: PERFILES_ALTA.PROVEEDOR, kind: null, titulo: 'Proveedor', sub: 'Vendo equipos y stock', dest: 'proveedor' },
];

export default function Login({ onDone }) {
  const [modo, setModo] = React.useState('login'); // 'login' | 'registro'
  const [alta, setAlta] = React.useState(ALTAS[0]);
  const [email, setEmail] = React.useState('');
  const [pwd, setPwd] = React.useState('');
  const [verPwd, setVerPwd] = React.useState(false);
  const [nombre, setNombre] = React.useState('');
  const [empresa, setEmpresa] = React.useState('');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // Segundo factor: cuando el backend pide un código (2FA del admin), `dosFA` guarda a qué número se
  // mandó (pista) y `codigo` lo que teclea el usuario. `null` = login normal de un paso.
  const [dosFA, setDosFA] = React.useState(null);
  const [codigo, setCodigo] = React.useState('');

  const entrar = async () => {
    setErr(''); setBusy(true);
    try {
      await login(email, pwd);
      onDone?.();
    } catch (e) {
      if (e && e.requiere_2fa) {
        // No es un fallo: la clave fue correcta y falta el código. Se pasa al segundo paso.
        setDosFA({ pista: e.pista });
        setErr('');
      } else {
        setErr(e.message || 'No se pudo iniciar sesión.');
      }
    } finally { setBusy(false); }
  };

  const verificar2FA = async () => {
    setErr(''); setBusy(true);
    try {
      await completarLogin2FA(email, codigo);
      onDone?.();
    } catch (e) { setErr(e.message || 'Código incorrecto.'); }
    finally { setBusy(false); }
  };

  const registrar = async () => {
    setErr(''); setBusy(true);
    try {
      await registrarBasico({
        nombre, email, password: pwd,
        perfil: alta.perfil,
        installerKind: alta.kind,
        empresa,
      });
      onDone?.(destinoTrasAlta(alta.perfil));
    } catch (e) { setErr(e.message || 'No se pudo registrar.'); }
    finally { setBusy(false); }
  };

  // Google Identity Services — login con Google (credenciales unificadas de la suite).
  const gbtn = React.useRef(null);
  const onDoneRef = React.useRef(onDone); onDoneRef.current = onDone;
  React.useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const cb = async (resp) => {
      setErr('');
      try { await loginWithGoogle(resp.credential); onDoneRef.current?.(); }
      catch (e) {
        if (e && e.requiere_2fa) {
          // La cuenta de Google es de un admin que exige segundo factor: el backend ya mandó el
          // código. Mismo paso que en `entrar`; el correo sale del error porque acá nadie lo tecleó.
          setEmail(e.email || '');
          setDosFA({ pista: e.pista });
          setErr('');
        } else {
          setErr(e.message || 'No se pudo entrar con Google.');
        }
      }
    };
    const init = () => {
      try {
        window.google?.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: cb });
        if (gbtn.current) {
          window.google?.accounts.id.renderButton(gbtn.current, {
            theme: THEME === 'light' ? 'outline' : 'filled_black', size: 'large', width: 320, text: 'continue_with',
          });
        }
      } catch { /* GIS no disponible */ }
    };
    if (window.google?.accounts?.id) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client'; s.async = true; s.defer = true; s.onload = init;
    document.head.appendChild(s);
  }, []); // eslint-disable-line

  const inp = {
    width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '10px 14px', color: C.text, fontSize: 13, boxSizing: 'border-box', marginBottom: 10,
  };

  const FLOW = [
    ['Cliente', 'Cotiza el techo o pide el estudio de detalle.'],
    ['Instala', 'Técnico, ingeniero o empresa RETIE. Una empresa también instala.'],
    ['Proveedor', 'Publica referencias y stock cuando lo tengas.'],
    ['Monitorea', 'Si la planta queda en telemetría, FluxAI mide lo real.'],
  ];

  return (
    <div style={{ minHeight: '100vh', background: C.dark, color: C.text }}>
      <div className="al-login-grid" style={{ minHeight: '100vh', maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', alignItems: 'stretch' }}>
        {/* IZQUIERDA — landing/propuesta de valor (la portada, con el login al lado) */}
        <div className="al-login-hero sh-grid-bg" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '48px 44px', borderRight: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 1, marginBottom: 14 }}>
            <span style={{ fontWeight: 900, fontSize: 30, color: C.heading, lineHeight: 1 }}>Solar</span>
            <span style={{ fontWeight: 900, fontSize: 30, color: C.yellow, lineHeight: 1 }}>Hub</span>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.teal, marginBottom: 12 }}>
            Colombia · sistemas fotovoltaicos
          </div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900, color: C.heading, lineHeight: 1.12, letterSpacing: '-0.5px' }}>
            Cotiza tu sistema solar con datos de <span style={{ color: C.yellow }}>tu techo</span>
          </h1>
          <p style={{ marginTop: 12, fontSize: 13.5, color: C.muted, maxWidth: 460, lineHeight: 1.55 }}>
            <strong style={{ color: C.heading }}>Solar Hub</strong> dimensiona con techo real, tarifa CREG y normativa RETIE.
            Entras, cotizas y te queda la propuesta. Si contratas el estudio, pasas a ingeniería de detalle.
          </p>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {FLOW.map(([t, d], i) => (
              <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 800, color: C.teal, background: `${C.teal}18`, border: `1px solid ${C.teal}44`, borderRadius: 5, padding: '2px 6px', minWidth: 22, textAlign: 'center' }}>{`0${i + 1}`}</span>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.heading }}>{t}</span>
                  <span style={{ fontSize: 11.5, color: C.muted }}> — {d}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* DERECHA — sesión (login) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px' }}>
        <div className="sh-fade-up" style={{ width: '100%', maxWidth: 400 }}>
        {/* Marca (visible en móvil cuando el hero se oculta) */}
        <div className="al-login-brand-m" style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
            <span style={{ fontWeight: 900, fontSize: 26, color: C.heading, lineHeight: 1 }}>Solar</span>
            <span style={{ fontWeight: 900, fontSize: 26, color: C.yellow, lineHeight: 1 }}>Hub</span>
          </div>
          <div style={{ fontSize: 10, color: C.teal, letterSpacing: 2, textTransform: 'uppercase', marginTop: 4 }}>
            Cotiza tu sistema solar en minutos
          </div>
        </div>

        <div className="sh-grid-bg" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '26px 24px' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.heading, marginBottom: 4 }}>
            {modo === 'login' ? 'Iniciar sesión' : `Inscribirme · ${alta.titulo}`}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.4 }}>
            {modo === 'login'
              ? 'Correo y contraseña, o Google. También puedes inscribirte como técnico, empresa instaladora o proveedor.'
              : alta.sub}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14 }}>
            {ALTAS.map((a) => {
              const on = modo === 'registro' && alta.titulo === a.titulo;
              return (
                <button key={a.titulo} type="button"
                  onClick={() => { setModo('registro'); setAlta(a); setErr(''); }}
                  style={{
                    textAlign: 'left', padding: '8px 9px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${on ? C.yellow : C.border}`,
                    background: on ? `${C.yellow}16` : 'transparent',
                    color: on ? C.heading : C.muted,
                  }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: on ? C.yellow : C.heading }}>{a.titulo}</div>
                  <div style={{ fontSize: 10, lineHeight: 1.3, marginTop: 2 }}>{a.sub}</div>
                </button>
              );
            })}
          </div>

          {modo === 'registro' && (
            <input style={inp} placeholder={alta.kind === TIPOS_INSTALADOR.EMPRESA ? 'Representante o contacto' : 'Nombre'}
              value={nombre} onChange={(e) => setNombre(e.target.value)} />
          )}
          {modo === 'registro' && alta.kind === TIPOS_INSTALADOR.EMPRESA && (
            <input style={inp} placeholder="Razón social de la empresa *"
              value={empresa} onChange={(e) => setEmpresa(e.target.value)} />
          )}
          <input style={inp} type="email" placeholder="Correo" value={email}
            onChange={(e) => setEmail(e.target.value)} autoFocus
            onKeyDown={(e) => e.key === 'Enter' && modo === 'registro' && registrar()} />
          <div style={{ position: 'relative', marginBottom: inp.marginBottom }}>
            <input style={{ ...inp, marginBottom: 0, paddingRight: 40 }} type={verPwd ? 'text' : 'password'}
              placeholder={modo === 'registro' ? 'Contraseña (mín. 8)' : 'Contraseña'} value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (modo === 'registro' ? registrar() : (dosFA ? verificar2FA() : entrar()))} />
            <OjoClave visible={verPwd} onToggle={() => setVerPwd((v) => !v)} />
          </div>

          {dosFA && (
            <input style={inp} type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              placeholder={`Código de WhatsApp${dosFA.pista ? ` (••${dosFA.pista})` : ''}`}
              value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))} autoFocus
              onKeyDown={(e) => e.key === 'Enter' && verificar2FA()} />
          )}

          {err && (
            <div style={{ fontSize: 11, color: C.red, background: `${C.red}15`, border: `1px solid ${C.red}33`, borderRadius: 6, padding: '7px 12px', marginBottom: 12 }}>{err}</div>
          )}

          {modo === 'login' ? (
            <button type="button" className="sh-btn sh-btn-primary sh-btn-block" onClick={entrar} disabled={busy}>
              {busy ? 'Ingresando…' : 'Ingresar →'}
            </button>
          ) : (
            <button type="button" className="sh-btn sh-btn-primary sh-btn-block" onClick={registrar} disabled={busy}>
              {busy ? 'Creando acceso…' : `Crear acceso · ${alta.titulo} →`}
            </button>
          )}

          {/* Separador + Google (credenciales unificadas de la suite) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0' }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 10, color: C.muted }}>o</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>
          <div ref={gbtn} style={{ display: 'flex', justifyContent: 'center', minHeight: 40 }} />

          <div style={{ marginTop: 14, textAlign: 'center', fontSize: 12, color: C.muted }}>
            {modo === 'login' ? (
              <>¿Primera vez? Elige arriba si eres cliente, técnico, empresa instaladora o proveedor.</>
            ) : (
              <>¿Ya tienes cuenta? <button onClick={() => { setModo('login'); setErr(''); }} style={linkBtn}>Iniciar sesión</button></>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
          ALEBAS Ingeniería S.A.S. · solar-hub.co<br />
          Al entrar aceptas el{' '}
          <a href="/privacidad" style={{ color: C.teal, fontWeight: 700 }}>tratamiento de datos</a>
          {' '}(Ley 1581) para cotizar e instalar tu sistema, y los{' '}
          <a href="/terminos" style={{ color: C.teal, fontWeight: 700 }}>términos y condiciones</a>.
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: '#EB5D0B', fontWeight: 700, cursor: 'pointer', fontSize: 12, padding: 0 };
